import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Calendar, Filter, TrendingUp, DollarSign,
  MousePointer, Eye, BarChart3, PieChart, Table2, LineChart
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getGoogleAdsClient, getDateRange } from '../lib/google-ads-client';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

interface ReportConfig {
  name: string;
  dateRange: string;
  metrics: string[];
  dimensions: string[];
  filters: Record<string, any>;
}

interface ReportData {
  dimensions: Record<string, string>;
  metrics: Record<string, number>;
}

export default function GoogleAdsReportingSuitePage() {
  const [loading, setLoading] = useState(false);
  const [reportConfig, setReportConfig] = useState<ReportConfig>({
    name: '',
    dateRange: 'last_30_days',
    metrics: ['impressions', 'clicks', 'cost', 'conversions'],
    dimensions: ['campaign'],
    filters: {}
  });
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [savedReports, setSavedReports] = useState<any[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [visualizationType, setVisualizationType] = useState<'table' | 'chart'>('table');
  const { company, setCompany } = useGoogleAdsCompany();

  const availableMetrics = [
    { id: 'impressions', label: 'Impressions', icon: Eye },
    { id: 'clicks', label: 'Clicks', icon: MousePointer },
    { id: 'cost', label: 'Cost', icon: DollarSign },
    { id: 'conversions', label: 'Conversions', icon: TrendingUp },
    { id: 'ctr', label: 'CTR', icon: BarChart3 },
    { id: 'avg_cpc', label: 'Avg CPC', icon: DollarSign },
    { id: 'conversion_rate', label: 'Conv. Rate', icon: TrendingUp },
    { id: 'cost_per_conversion', label: 'Cost/Conv', icon: DollarSign }
  ];

  const availableDimensions = [
    { id: 'campaign', label: 'Campaign' },
    { id: 'ad_group', label: 'Ad Group' },
    { id: 'keyword', label: 'Keyword' },
    { id: 'device', label: 'Device' },
    { id: 'location', label: 'Location' },
    { id: 'date', label: 'Date' },
    { id: 'hour', label: 'Hour of Day' },
    { id: 'day_of_week', label: 'Day of Week' }
  ];

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_14_days', label: 'Last 14 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'last_90_days', label: 'Last 90 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_year', label: 'This Year' }
  ];

  useEffect(() => {
    loadSavedReports();
    loadCustomerAccounts();
  }, [company]);

  const loadCustomerAccounts = async () => {
    if (!company) return;
    const { data } = await supabase
      .from('google_ads_customer_mapping')
      .select('*')
      .eq('company', company)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();
    
    if (data) {
      setSelectedCustomerId(data.customer_id);
    }
  };

  const loadSavedReports = async () => {
    const { data } = await supabase
      .from('google_ads_saved_reports')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (data) {
      setSavedReports(data);
    }
  };

  const generateReport = async () => {
    if (!selectedCustomerId) {
      alert('Please configure a Google Ads customer account first');
      return;
    }

    setLoading(true);
    try {
      const client = getGoogleAdsClient(selectedCustomerId);
      
      // Get date range
      const dateRange = getDateRangeFromConfig(reportConfig.dateRange);
      
      // Fetch data based on dimensions
      let data: ReportData[] = [];
      
      if (reportConfig.dimensions.includes('campaign')) {
        const campaigns = await client.listCampaigns(selectedCustomerId);
        const metrics = await client.getCampaignMetrics(
          selectedCustomerId,
          campaigns.map(c => c.id),
          dateRange
        );
        
        data = aggregateReportData(metrics, reportConfig);
      }

      setReportData(data);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeFromConfig = (rangeKey: string) => {
    const today = new Date();
    const startDate = new Date();
    
    switch (rangeKey) {
      case 'today':
        break;
      case 'yesterday':
        startDate.setDate(today.getDate() - 1);
        break;
      case 'last_7_days':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'last_14_days':
        startDate.setDate(today.getDate() - 14);
        break;
      case 'last_30_days':
        startDate.setDate(today.getDate() - 30);
        break;
      case 'last_90_days':
        startDate.setDate(today.getDate() - 90);
        break;
      default:
        startDate.setDate(today.getDate() - 30);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };

  const aggregateReportData = (metrics: any[], config: ReportConfig): ReportData[] => {
    const aggregated = new Map<string, any>();

    metrics.forEach(metric => {
      const key = config.dimensions.map(dim => {
        if (dim === 'campaign') return metric.campaignName;
        if (dim === 'date') return metric.date;
        return '';
      }).join('|');

      if (!aggregated.has(key)) {
        aggregated.set(key, {
          dimensions: {},
          metrics: {
            impressions: 0,
            clicks: 0,
            cost: 0,
            conversions: 0,
            ctr: 0,
            avg_cpc: 0
          }
        });
      }

      const entry = aggregated.get(key);
      entry.metrics.impressions += metric.impressions;
      entry.metrics.clicks += metric.clicks;
      entry.metrics.cost += metric.costMicros / 1000000;
      entry.metrics.conversions += metric.conversions;
      
      // Set dimensions
      if (config.dimensions.includes('campaign')) {
        entry.dimensions.campaign = metric.campaignName;
      }
      if (config.dimensions.includes('date')) {
        entry.dimensions.date = metric.date;
      }
    });

    // Calculate derived metrics
    aggregated.forEach(entry => {
      if (entry.metrics.impressions > 0) {
        entry.metrics.ctr = (entry.metrics.clicks / entry.metrics.impressions) * 100;
      }
      if (entry.metrics.clicks > 0) {
        entry.metrics.avg_cpc = entry.metrics.cost / entry.metrics.clicks;
      }
      if (entry.metrics.conversions > 0) {
        entry.metrics.cost_per_conversion = entry.metrics.cost / entry.metrics.conversions;
      }
      if (entry.metrics.clicks > 0) {
        entry.metrics.conversion_rate = (entry.metrics.conversions / entry.metrics.clicks) * 100;
      }
    });

    return Array.from(aggregated.values());
  };

  const exportReport = (format: 'csv' | 'xlsx' | 'pdf') => {
    if (reportData.length === 0) {
      alert('No data to export');
      return;
    }

    if (format === 'csv') {
      exportAsCSV();
    } else {
      alert(`${format.toUpperCase()} export coming soon`);
    }
  };

  const exportAsCSV = () => {
    const headers = [
      ...reportConfig.dimensions,
      ...reportConfig.metrics
    ].join(',');

    const rows = reportData.map(row => {
      const dimensionValues = reportConfig.dimensions.map(dim => 
        row.dimensions[dim] || ''
      );
      const metricValues = reportConfig.metrics.map(metric => 
        row.metrics[metric] || 0
      );
      return [...dimensionValues, ...metricValues].join(',');
    });

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `google-ads-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const saveReport = async () => {
    if (!reportConfig.name) {
      alert('Please enter a report name');
      return;
    }

    const { error } = await supabase
      .from('google_ads_saved_reports')
      .insert({
        name: reportConfig.name,
        config: reportConfig,
        customer_id: selectedCustomerId
      });

    if (!error) {
      alert('Report saved successfully');
      loadSavedReports();
    }
  };

  const loadSavedReport = (report: any) => {
    setReportConfig(report.config);
    generateReport();
  };

  const toggleMetric = (metricId: string) => {
    setReportConfig(prev => ({
      ...prev,
      metrics: prev.metrics.includes(metricId)
        ? prev.metrics.filter(m => m !== metricId)
        : [...prev.metrics, metricId]
    }));
  };

  const toggleDimension = (dimensionId: string) => {
    setReportConfig(prev => ({
      ...prev,
      dimensions: prev.dimensions.includes(dimensionId)
        ? prev.dimensions.filter(d => d !== dimensionId)
        : [...prev.dimensions, dimensionId]
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <select
          value={company}
          onChange={(e) =>
            setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')
          }
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Google Ads Reporting Suite</h1>
          <p className="text-gray-600 mt-1">
            Build custom reports with flexible metrics and dimensions
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Configuration */}
        <div className="lg:col-span-1 space-y-6">
          {/* Report Name */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Report Configuration</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Name
                </label>
                <input
                  type="text"
                  value={reportConfig.name}
                  onChange={(e) => setReportConfig({ ...reportConfig, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Monthly Performance"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Range
                </label>
                <select
                  value={reportConfig.dateRange}
                  onChange={(e) => setReportConfig({ ...reportConfig, dateRange: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {dateRangeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Metrics Selection */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Metrics</h3>
            <div className="space-y-2">
              {availableMetrics.map(metric => {
                const Icon = metric.icon;
                return (
                  <label key={metric.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportConfig.metrics.includes(metric.id)}
                      onChange={() => toggleMetric(metric.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Icon className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">{metric.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Dimensions Selection */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Dimensions</h3>
            <div className="space-y-2">
              {availableDimensions.map(dimension => (
                <label key={dimension.id} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reportConfig.dimensions.includes(dimension.id)}
                    onChange={() => toggleDimension(dimension.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{dimension.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={generateReport}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
            <button
              onClick={saveReport}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Save Report
            </button>
          </div>

          {/* Saved Reports */}
          {savedReports.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Saved Reports</h3>
              <div className="space-y-2">
                {savedReports.map(report => (
                  <button
                    key={report.id}
                    onClick={() => loadSavedReport(report)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 border border-gray-200"
                  >
                    <div className="text-sm font-medium text-gray-900">{report.name}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(report.created_at).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Report Display */}
        <div className="lg:col-span-2 space-y-6">
          {/* Export Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Report Results</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setVisualizationType('table')}
                  className={`px-3 py-1 rounded ${
                    visualizationType === 'table'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Table2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setVisualizationType('chart')}
                  className={`px-3 py-1 rounded ${
                    visualizationType === 'chart'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <LineChart className="w-4 h-4" />
                </button>
              </div>
            </div>

            {reportData.length > 0 && (
              <div className="flex items-center space-x-2 mb-4">
                <button
                  onClick={() => exportReport('csv')}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Download className="w-4 h-4" />
                  <span>Export CSV</span>
                </button>
                <button
                  onClick={() => exportReport('xlsx')}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4" />
                  <span>Export Excel</span>
                </button>
                <button
                  onClick={() => exportReport('pdf')}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Download className="w-4 h-4" />
                  <span>Export PDF</span>
                </button>
              </div>
            )}

            {/* Table View */}
            {visualizationType === 'table' && reportData.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {reportConfig.dimensions.map(dim => (
                        <th
                          key={dim}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {availableDimensions.find(d => d.id === dim)?.label || dim}
                        </th>
                      ))}
                      {reportConfig.metrics.map(metric => (
                        <th
                          key={metric}
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {availableMetrics.find(m => m.id === metric)?.label || metric}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        {reportConfig.dimensions.map(dim => (
                          <td key={dim} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.dimensions[dim] || '-'}
                          </td>
                        ))}
                        {reportConfig.metrics.map(metric => (
                          <td key={metric} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {metric === 'cost' || metric === 'avg_cpc' || metric === 'cost_per_conversion'
                              ? `$${row.metrics[metric]?.toFixed(2) || '0.00'}`
                              : metric === 'ctr' || metric === 'conversion_rate'
                              ? `${row.metrics[metric]?.toFixed(2) || '0.00'}%`
                              : row.metrics[metric]?.toLocaleString() || '0'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Chart View Placeholder */}
            {visualizationType === 'chart' && reportData.length > 0 && (
              <div className="h-96 flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-center">
                  <LineChart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Chart visualization coming soon</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {reportData.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No data to display</p>
                <p className="text-sm text-gray-500">
                  Configure your report and click "Generate Report" to see results
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
